import React from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - FutureXSkills</title>
        <meta name="description" content="Privacy Policy for FutureXSkills - Learn how we collect, use, and protect your personal information." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://futurexskills.com/privacy-policy" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold gradient-text mb-4">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: November 6, 2025</p>
        </div>

        <div className="glass-card p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">1. Information We Collect</h2>
            <div className="text-gray-700 space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
              <p>When you interact with our website, we may collect:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Email address when subscribing to newsletters</li>
                <li>Name and contact information when filling out forms</li>
                <li>Comments and feedback when engaging with our content</li>
                <li>Course enrollment data when redirected to Udemy</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-6">Usage Data</h3>
              <p>We automatically collect certain information when you visit our website:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>IP address and browser type</li>
                <li>Pages visited and time spent on site</li>
                <li>Referring website and search terms used</li>
                <li>Device information and screen resolution</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">2. How We Use Your Information</h2>
            <div className="text-gray-700">
              <p className="mb-4">We use the collected information for:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Educational Services:</strong> Providing course information and learning resources</li>
                <li><strong>Communication:</strong> Sending updates about new courses and educational content</li>
                <li><strong>Analytics:</strong> Understanding user behavior to improve our website and services</li>
                <li><strong>Marketing:</strong> Promoting relevant courses and educational opportunities</li>
                <li><strong>Legal Compliance:</strong> Meeting legal obligations and protecting our rights</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">3. Third-Party Services</h2>
            <div className="text-gray-700 space-y-4">
              <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Analytics</h3>
                <p>We use Google Analytics to understand how visitors interact with our website. This helps us improve user experience and content quality.</p>
              </div>
              
              <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Course Platform</h3>
                <p>When you click on course links, you may be redirected to Udemy, which has its own privacy policy and data collection practices.</p>
              </div>
              
              <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Hosting & CDN</h3>
                <p>Our website is hosted on Amazon Web Services (AWS) with CloudFront CDN for fast, secure delivery of content globally.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">4. Data Security</h2>
            <div className="text-gray-700">
              <p className="mb-4">We implement appropriate security measures to protect your personal information:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>SSL encryption for data transmission</li>
                <li>Secure hosting infrastructure on AWS</li>
                <li>Regular security updates and monitoring</li>
                <li>Limited access to personal data on a need-to-know basis</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">5. Your Rights</h2>
            <div className="text-gray-700">
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data we hold</li>
                <li><strong>Correction:</strong> Request correction of inaccurate personal data</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Portability:</strong> Request your data in a portable format</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">6. Cookies and Tracking</h2>
            <div className="text-gray-700">
              <p className="mb-4">We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and user behavior</li>
                <li>Provide personalized content and advertisements</li>
                <li>Ensure website security and prevent fraud</li>
              </ul>
              <p className="mt-4">You can control cookies through your browser settings, though this may affect website functionality.</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">7. Contact Information</h2>
            <div className="text-gray-700">
              <p className="mb-4">For privacy-related questions or requests, please contact us:</p>
              <div className="bg-blue-500/10 p-4 rounded-lg">
                <p><strong>Email:</strong> <a href="mailto:futurexskill@gmail.com" className="text-blue-600 hover:text-blue-500">futurexskill@gmail.com</a></p>
                <p><strong>Website:</strong> <a href="https://futurexskills.com" className="text-blue-600 hover:text-blue-500">https://futurexskills.com</a></p>
                <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-600 mb-4">8. Changes to This Policy</h2>
            <div className="text-gray-700">
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.</p>
            </div>
          </section>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            By using our website, you consent to the collection and use of information as described in this Privacy Policy.
          </p>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;